import {
  formatAbsoluteRecordingTime,
  formatNavigationTimelineTickLabel,
  isNavigationTimelineSlidingWindow,
  resolveNavigationTimelineVisibleRange,
} from "./eegNavigationTimelineUtils";

export interface EEGViewerLiveStatus {
  studyElapsedLabel: string;
  studyTotalLabel: string;
  pageCenterWallClock: string | null;
  /** Compact clock for narrow patient bar (time + UTC suffix). */
  pageCenterWallClockShort: string | null;
  navWindowStartLabel: string | null;
  navWindowEndLabel: string | null;
  showNavWindow: boolean;
}

/** Wall-clock label for the center of the current waveform page (matches canvas overlay). */
export function formatEEGViewerPageCenterWallClock(
  currentTimeSeconds: number,
  timeScaleSeconds: number,
  recordingStartMs?: number | null,
): string | null {
  if (
    typeof recordingStartMs !== "number" ||
    !Number.isFinite(recordingStartMs) ||
    recordingStartMs <= 0
  ) {
    return null;
  }

  const anchorSeconds =
    Math.max(0, currentTimeSeconds) + Math.max(0.1, timeScaleSeconds) / 2;
  const absoluteDate = new Date(recordingStartMs + anchorSeconds * 1000);
  return absoluteDate.toISOString().replace("T", " ").replace("Z", " UTC");
}

/** Time-of-day only — full ISO remains on the segment title attribute. */
export function formatEEGViewerPageCenterWallClockShort(
  pageCenterWallClock: string | null,
): string | null {
  if (!pageCenterWallClock) {
    return null;
  }

  const match = pageCenterWallClock.match(/(\d{2}:\d{2}:\d{2})/);
  return match ? `${match[1]} UTC` : pageCenterWallClock;
}

export function resolveEEGViewerWaveformTextOverlays(
  showToolbarControls: boolean,
  showScaleMarkersEnabled: boolean,
  liveStatusRailActive: boolean,
): { showScaleMarkers: boolean; persistentTimestamp: boolean } {
  if (liveStatusRailActive) {
    return {
      showScaleMarkers: false,
      persistentTimestamp: false,
    };
  }

  return {
    showScaleMarkers: showScaleMarkersEnabled && !showToolbarControls,
    persistentTimestamp: !showToolbarControls,
  };
}

export function buildEEGViewerLiveStatus(params: {
  currentTime: number;
  totalDuration: number;
  timeScale: number;
  recordingStartMs?: number | null;
}): EEGViewerLiveStatus | null {
  const totalDuration = Math.max(0, params.totalDuration);
  if (totalDuration <= 0) {
    return null;
  }

  const currentTime = Math.max(0, Math.min(totalDuration, params.currentTime));
  const timeScale = Math.max(0.1, params.timeScale || 10);
  const showNavWindow = isNavigationTimelineSlidingWindow(totalDuration);
  const navRange = resolveNavigationTimelineVisibleRange({
    totalDuration,
    playheadTime: currentTime,
    viewportDuration: timeScale,
  });
  const pageCenterWallClock = formatEEGViewerPageCenterWallClock(
    currentTime,
    timeScale,
    params.recordingStartMs,
  );

  return {
    studyElapsedLabel: formatAbsoluteRecordingTime(currentTime),
    studyTotalLabel: formatAbsoluteRecordingTime(totalDuration),
    pageCenterWallClock,
    pageCenterWallClockShort:
      formatEEGViewerPageCenterWallClockShort(pageCenterWallClock),
    navWindowStartLabel: showNavWindow
      ? formatNavigationTimelineTickLabel(
          navRange.rangeStart,
          params.recordingStartMs,
        )
      : null,
    navWindowEndLabel: showNavWindow
      ? formatNavigationTimelineTickLabel(
          navRange.rangeEnd,
          params.recordingStartMs,
        )
      : null,
    showNavWindow,
  };
}
